import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { RoutesEnum } from '../lib/routes'
import { me, logoutEverywhere } from '../lib/api'
import { useLang } from '../lib/useLang'

export default function Settings() {
  const navigate = useNavigate()
  const { t } = useLang()

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      badge: t('settings.trial'),
      price: t('settings.priceFree'),
      period: t('settings.duringTrial'),
      features: t('settings.starterFeatures'),
      current: true,
    },
    {
      id: 'growth',
      name: 'Growth',
      badge: t('settings.trial'),
      price: t('settings.priceFree'),
      period: t('settings.duringTrial'),
      features: t('settings.growthFeatures'),
      popular: true,
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      badge: t('settings.trial'),
      price: t('settings.priceCustom'),
      period: t('settings.contactUsPrice'),
      features: t('settings.enterpriseFeatures'),
    },
  ]
  const [activeTab, setActiveTab] = useState('general')
  const [userEmail, setUserEmail] = useState('')
  const [selectedCRM, setSelectedCRM] = useState('none')
  const [showCRMDropdown, setShowCRMDropdown] = useState(false)

  // Change password state
  const [currentPwd, setCurrentPwd] = useState('')
  const [newPwd, setNewPwd] = useState('')
  const [confirmPwd, setConfirmPwd] = useState('')
  const [pwdChanging, setPwdChanging] = useState(false)
  const [pwdMsg, setPwdMsg] = useState(null) // { ok: bool, text: string }
  const [loggingOutAll, setLoggingOutAll] = useState(false)

  // Delete account state
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // API Keys state
  const [apiKeys, setApiKeys] = useState([])
  const [apiKeysLoading, setApiKeysLoading] = useState(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [generatedKey, setGeneratedKey] = useState(null) // shown once after creation
  const [keyCreating, setKeyCreating] = useState(false)
  const [keyCopied, setKeyCopied] = useState(false)

  const handleChangePassword = useCallback(async () => {
    if (newPwd !== confirmPwd) return setPwdMsg({ ok: false, text: t('settings.passwordsNoMatch') })
    if (newPwd.length < 8) return setPwdMsg({ ok: false, text: t('settings.passwordMinLength') })
    setPwdChanging(true)
    setPwdMsg(null)
    try {
      const res = await fetch('/api/settings/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword: currentPwd, newPassword: newPwd }),
      })
      const data = await res.json()
      if (!res.ok) return setPwdMsg({ ok: false, text: data.error || t('settings.failedChangePassword') })
      setPwdMsg({ ok: true, text: t('settings.passwordChanged') })
      setCurrentPwd('')
      setNewPwd('')
      setConfirmPwd('')
    } finally {
      setPwdChanging(false)
    }
  }, [currentPwd, newPwd, confirmPwd])

  const handleLogoutEverywhere = useCallback(async () => {
    setLoggingOutAll(true)
    try {
      await logoutEverywhere()
      navigate(RoutesEnum.AUTH)
    } finally {
      setLoggingOutAll(false)
    }
  }, [navigate])

  const handleDeleteAccount = useCallback(async () => {
    setDeleting(true)
    try {
      await fetch('/api/settings/account', { method: 'DELETE' })
      navigate(RoutesEnum.AUTH)
    } finally {
      setDeleting(false)
    }
  }, [navigate])

  const loadApiKeys = useCallback(async () => {
    setApiKeysLoading(true)
    try {
      const res = await fetch('/api/settings/api-keys')
      if (res.ok) setApiKeys(await res.json())
    } finally {
      setApiKeysLoading(false)
    }
  }, [])

  useEffect(() => {
    me().then(d => setUserEmail(d.email || '')).catch(() => {})
  }, [])

  useEffect(() => {
    if (activeTab === 'general') loadApiKeys()
    setPwdMsg(null)
  }, [activeTab, loadApiKeys])

  const handleCreateKey = useCallback(async () => {
    setKeyCreating(true)
    try {
      const res = await fetch('/api/settings/api-keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newKeyName.trim() || null }),
      })
      const data = await res.json()
      if (data.key) {
        setGeneratedKey(data.key)
        setNewKeyName('')
        loadApiKeys()
      }
    } finally {
      setKeyCreating(false)
    }
  }, [newKeyName, loadApiKeys])

  const handleRevokeKey = useCallback(async (id) => {
    const res = await fetch(`/api/settings/api-keys/${id}`, { method: 'DELETE' })
    if (res.ok) setApiKeys(prev => prev.filter(k => k.id !== id))
  }, [])

  const handleCopyKey = useCallback(() => {
    if (!generatedKey) return
    navigator.clipboard.writeText(generatedKey)
    setKeyCopied(true)
    setTimeout(() => setKeyCopied(false), 2000)
  }, [generatedKey])

  const crmOptions = [
    { id: 'none', label: t('settings.crmNone') },
    { id: 'hubspot', label: 'HubSpot' },
    { id: 'zoho', label: 'Zoho' },
    { id: 'salesforce', label: 'Salesforce' },
  ]

  const tabs = [
    { id: 'general', label: t('settings.general'), icon: (
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
        <circle cx='12' cy='12' r='3' />
        <path d='M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z' />
      </svg>
    )},
    { id: 'plans', label: t('settings.subscription'), icon: (
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
        <rect x='1' y='4' width='22' height='16' rx='2' ry='2' />
        <line x1='1' y1='10' x2='23' y2='10' />
      </svg>
    )},
  ]

  return (
    <div
      className='min-h-[calc(100vh-76px)] py-8'
      style={{
        background: `radial-gradient(ellipse 100% 60% at 50% 20%, rgba(255, 90, 31, 0.12) 0%, transparent 50%),
                     radial-gradient(ellipse 80% 50% at 50% 50%, rgba(196, 61, 16, 0.08) 0%, transparent 60%),
                     #F5F1E8`,
      }}
    >
      <div className='mx-auto max-w-7xl px-6 sm:px-10'>
        {/* Header */}
        <div className='mb-8'>
          <button
            onClick={() => navigate(RoutesEnum.APP)}
            className='mb-4 flex items-center gap-2 text-sm text-ink transition-colors hover:text-ink'
          >
            <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <path d='m15 18-6-6 6-6' />
            </svg>
            {t('settings.backToProjects')}
          </button>
          <h1 className='text-3xl font-medium tracking-tight text-ink'>{t('settings.title')}</h1>
          <p className='mt-2 text-sm text-ink'>{t('settings.subtitle')}</p>
        </div>

        {/* Tabs */}
        <div className='mb-8 flex gap-2 overflow-x-auto'>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm transition-all ${
                activeTab === tab.id
                  ? 'bg-accent/15 text-accent'
                  : 'text-ink hover:bg-ink/[0.05] hover:text-ink'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div
          className='rounded-2xl p-6'
          style={{
            background: 'rgba(14, 14, 16, 0.02)',
            border: '1px solid rgba(14, 14, 16, 0.04)',
          }}
        >
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className='space-y-6'>
              <div>
                <h3 className='mb-4 text-lg font-medium text-ink'>{t('settings.account')}</h3>
                <div className='space-y-4'>
                  <div>
                    <label className='mb-2 block text-sm text-ink'>{t('settings.email')}</label>
                    <input
                      type='email'
                      value={userEmail}
                      placeholder='your@email.com'
                      disabled
                      className='w-full rounded-xl bg-ink/[0.03] px-4 py-3 text-sm text-ink outline-none'
                      style={{ border: '1px solid rgba(14, 14, 16, 0.08)' }}
                    />
                    <p className='mt-1 text-xs text-ink'>{t('settings.emailCannotChange')}</p>
                  </div>
                </div>
              </div>

              <div className='border-t border-ink/10 pt-6'>
                <h3 className='mb-4 text-lg font-medium text-ink'>{t('settings.security')}</h3>
                <div className='space-y-4'>
                  <div>
                    <label className='mb-2 block text-sm text-ink'>{t('settings.currentPassword')}</label>
                    <input
                      type='password'
                      value={currentPwd}
                      onChange={e => setCurrentPwd(e.target.value)}
                      placeholder={t('settings.enterCurrentPassword')}
                      className='mod-field w-full rounded-xl px-4 py-3 text-sm text-ink'
                    />
                  </div>
                  <div>
                    <label className='mb-2 block text-sm text-ink'>{t('settings.newPassword')}</label>
                    <input
                      type='password'
                      value={newPwd}
                      onChange={e => setNewPwd(e.target.value)}
                      placeholder={t('settings.enterNewPassword')}
                      className='mod-field w-full rounded-xl px-4 py-3 text-sm text-ink'
                    />
                  </div>
                  <div>
                    <label className='mb-2 block text-sm text-ink'>{t('settings.confirmNewPassword')}</label>
                    <input
                      type='password'
                      value={confirmPwd}
                      onChange={e => setConfirmPwd(e.target.value)}
                      placeholder={t('settings.confirmNewPasswordPlaceholder')}
                      className='mod-field w-full rounded-xl px-4 py-3 text-sm text-ink'
                    />
                  </div>
                  {pwdMsg && (
                    <p className={`text-sm ${pwdMsg.ok ? 'text-accent' : 'text-red-600'}`}>{pwdMsg.text}</p>
                  )}
                  <button
                    onClick={handleChangePassword}
                    disabled={pwdChanging || !currentPwd || !newPwd || !confirmPwd}
                    className='rounded-xl bg-accent/15 px-4 py-2.5 text-sm text-accent transition-all hover:bg-accent/15 disabled:opacity-50'
                  >
                    {pwdChanging ? t('settings.saving') : t('settings.changePassword')}
                  </button>

                  <div className='border-t border-ink/10 pt-4'>
                    <p className='mb-3 text-sm text-muted'>{t('settings.logoutEverywhereHint')}</p>
                    <button
                      onClick={handleLogoutEverywhere}
                      disabled={loggingOutAll}
                      className='rounded-xl border border-ink/10 px-4 py-2.5 text-sm text-ink transition-all hover:bg-ink/[0.03] disabled:opacity-50'
                    >
                      {loggingOutAll ? t('settings.saving') : t('settings.logoutEverywhere')}
                    </button>
                  </div>
                </div>
              </div>

              <div className='border-t border-ink/10 pt-6'>
                <h3 className='mb-4 text-lg font-medium text-ink'>{t('settings.integrations')}</h3>
                <div className='space-y-4'>
                  <div>
                    <label className='mb-2 block text-sm text-ink'>{t('settings.crmSystem')}</label>
                    <div className='relative'>
                      <button
                        onClick={() => setShowCRMDropdown(!showCRMDropdown)}
                        className='flex w-full items-center justify-between rounded-xl bg-ink/[0.03] px-4 py-3 text-sm text-ink transition-all hover:bg-ink/[0.05]'
                        style={{ border: '1px solid rgba(14, 14, 16, 0.05)' }}
                      >
                        <span className='flex items-center gap-2'>
                          <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
                            <path d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' />
                            <circle cx='9' cy='7' r='4' />
                            <path d='M23 21v-2a4 4 0 0 0-3-3.87' />
                            <path d='M16 3.13a4 4 0 0 1 0 7.75' />
                          </svg>
                          {selectedCRM === 'none' ? t('settings.connectCRM') : crmOptions.find(c => c.id === selectedCRM)?.label}
                        </span>
                        <svg
                          width='16'
                          height='16'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='2'
                          className={`transition-transform ${showCRMDropdown ? 'rotate-180' : ''}`}
                        >
                          <path d='m6 9 6 6 6-6' />
                        </svg>
                      </button>
                      {showCRMDropdown && (
                        <div
                          className='absolute left-0 right-0 top-full z-10 mt-2 overflow-hidden rounded-xl'
                          style={{
                            background: 'rgba(20, 20, 25, 0.98)',
                            border: '1px solid rgba(14, 14, 16, 0.07)',
                            boxShadow: '0 8px 32px rgba(14, 14, 16, 0.20)',
                          }}
                        >
                          {crmOptions.map(option => (
                            <button
                              key={option.id}
                              onClick={() => {
                                setSelectedCRM(option.id)
                                setShowCRMDropdown(false)
                              }}
                              className={`flex w-full items-center gap-2 px-4 py-3 text-left text-sm transition-colors hover:bg-ink/[0.05] ${
                                selectedCRM === option.id ? 'bg-ink/[0.03] text-ink' : 'text-ink'
                              }`}
                            >
                              {selectedCRM === option.id && (
                                <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' className='text-ink'>
                                  <path d='M20 6L9 17l-5-5' />
                                </svg>
                              )}
                              <span className={selectedCRM === option.id ? '' : 'ml-5'}>{option.label}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className='mt-2 text-xs text-ink'>
                      {selectedCRM === 'none'
                        ? t('settings.connectCRMHint')
                        : t('settings.connectedTo', { crm: crmOptions.find(c => c.id === selectedCRM)?.label })}
                    </p>
                  </div>
                </div>
              </div>

              <div className='border-t border-ink/10 pt-6'>
                <h3 className='mb-1 text-lg font-medium text-ink'>{t('settings.apiKeys')}</h3>
                <p className='mb-4 text-sm text-ink'>
                  {t('settings.apiKeysDesc')}{' '}
                  {t('settings.apiKeysPassAs')} <code className='rounded bg-ink/[0.03] px-1.5 py-0.5 font-mono text-xs text-ink'>Authorization: Bearer &lt;key&gt;</code>
                </p>

                {/* Generated key banner — shown once */}
                {generatedKey && (
                  <div className='mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4'>
                    <div className='mb-2 flex items-center gap-2 text-sm font-medium text-amber-600'>
                      <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                        <path d='M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' />
                        <line x1='12' y1='9' x2='12' y2='13' /><line x1='12' y1='17' x2='12.01' y2='17' />
                      </svg>
                      {t('settings.copyKeyNow')}
                    </div>
                    <div className='flex items-center gap-2'>
                      <code className='flex-1 overflow-x-auto rounded-lg bg-black/30 px-3 py-2 font-mono text-xs text-ink'>
                        {generatedKey}
                      </code>
                      <button
                        onClick={handleCopyKey}
                        className='flex-shrink-0 rounded-lg bg-amber-500/20 px-3 py-2 text-xs text-amber-600 transition-all hover:bg-amber-500/30'
                      >
                        {keyCopied ? t('settings.copied') : t('settings.copy')}
                      </button>
                      <button
                        onClick={() => setGeneratedKey(null)}
                        className='flex-shrink-0 rounded-lg px-3 py-2 text-xs text-ink transition-all hover:text-ink'
                      >
                        {t('settings.dismiss')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Existing keys list */}
                {apiKeysLoading ? (
                  <div className='py-4 text-center text-sm text-ink'>{t('settings.loading')}</div>
                ) : apiKeys.length > 0 ? (
                  <div className='mb-4 space-y-2'>
                    {apiKeys.map(k => (
                      <div
                        key={k.id}
                        className='flex items-center justify-between rounded-xl bg-ink/[0.03] px-4 py-3'
                        style={{ border: '1px solid rgba(14, 14, 16, 0.05)' }}
                      >
                        <div className='min-w-0'>
                          <div className='flex items-center gap-2'>
                            <code className='font-mono text-sm text-ink'>{k.key_prefix}•••</code>
                            {k.name && <span className='text-sm text-ink'>{k.name}</span>}
                          </div>
                          <div className='mt-0.5 text-xs text-ink'>
                            {t('settings.created')} {new Date(k.created_at).toLocaleDateString()}
                            {k.last_used_at && <> · {t('settings.lastUsed')} {new Date(k.last_used_at).toLocaleDateString()}</>}
                          </div>
                        </div>
                        <button
                          onClick={() => handleRevokeKey(k.id)}
                          className='ml-4 flex-shrink-0 text-xs text-red-600/70 transition-all hover:text-red-600'
                        >
                          {t('settings.revoke')}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className='mb-4 rounded-xl bg-ink/[0.03] py-6 text-center text-sm text-ink'
                    style={{ border: '1px solid rgba(14, 14, 16, 0.04)' }}
                  >
                    {t('settings.noApiKeys')}
                  </div>
                )}

                {/* Create new key */}
                <div className='flex gap-2'>
                  <input
                    type='text'
                    value={newKeyName}
                    onChange={e => setNewKeyName(e.target.value)}
                    placeholder={t('settings.keyNamePlaceholder')}
                    className='flex-1 rounded-xl bg-ink/[0.03] px-4 py-2.5 text-sm text-ink outline-none transition-colors focus:bg-ink/[0.05]'
                    style={{ border: '1px solid rgba(14, 14, 16, 0.05)' }}
                    onKeyDown={e => e.key === 'Enter' && !keyCreating && handleCreateKey()}
                  />
                  <button
                    onClick={handleCreateKey}
                    disabled={keyCreating}
                    className='rounded-xl bg-accent/15 px-4 py-2.5 text-sm text-accent transition-all hover:bg-accent/15 disabled:opacity-50'
                  >
                    {keyCreating ? t('settings.generating') : t('settings.generateKey')}
                  </button>
                </div>
              </div>

              <div className='border-t border-ink/10 pt-6'>
                {!deleteConfirm ? (
                  <button
                    onClick={() => setDeleteConfirm(true)}
                    className='rounded-xl px-4 py-2.5 text-sm text-red-600 transition-all hover:bg-red-500/10'
                    style={{ border: '1px solid rgba(239, 68, 68, 0.2)' }}
                  >
                    {t('settings.deleteAccount')}
                  </button>
                ) : (
                  <div className='rounded-xl border border-red-500/30 bg-red-500/10 p-4'>
                    <p className='mb-3 text-sm text-red-600'>{t('settings.deleteWarning')}</p>
                    <div className='flex gap-2'>
                      <button
                        onClick={handleDeleteAccount}
                        disabled={deleting}
                        className='rounded-xl bg-red-500/20 px-4 py-2 text-sm text-red-600 transition-all hover:bg-red-500/30 disabled:opacity-50'
                      >
                        {deleting ? t('settings.deleting') : t('settings.yesDelete')}
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(false)}
                        className='rounded-xl px-4 py-2 text-sm text-ink transition-all hover:text-ink'
                      >
                        {t('projects.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Plans Tab */}
          {activeTab === 'plans' && (
            <div>
              <h3 className='mb-6 text-lg font-medium text-ink'>{t('settings.choosePlan')}</h3>
              <div className='grid gap-4 md:grid-cols-3'>
                {plans.map(plan => (
                  <div
                    key={plan.id}
                    className={`relative rounded-2xl p-6 transition-all ${
                      plan.current ? 'ring-2 ring-accent/50' : ''
                    }`}
                    style={{
                      background: plan.popular
                        ? 'linear-gradient(135deg, rgba(255, 90, 31, 0.1) 0%, rgba(196, 61, 16, 0.05) 100%)'
                        : 'rgba(14, 14, 16, 0.02)',
                      border: `1px solid ${plan.popular ? 'rgba(255, 90, 31, 0.2)' : 'rgba(14, 14, 16, 0.04)'}`,
                    }}
                  >
                    {/* Badges */}
                    <div className='mb-3 flex flex-wrap gap-2'>
                      <span className='rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-semibold text-accent'>
                        {plan.badge}
                      </span>
                      {plan.popular && (
                        <span className='rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-semibold text-ink'>
                          {t('settings.popular')}
                        </span>
                      )}
                      {plan.current && (
                        <span className='rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-semibold text-ink'>
                          {t('settings.current')}
                        </span>
                      )}
                    </div>
                    <h4 className='mb-2 text-lg font-medium text-ink'>{plan.name}</h4>
                    <div className='mb-4'>
                      <span className='text-3xl font-bold text-ink'>{plan.price}</span>
                      <span className='ml-1 text-sm text-ink'>{plan.period}</span>
                    </div>
                    <ul className='mb-6 space-y-2'>
                      {plan.features.map((feature, i) => (
                        <li key={i} className='flex items-center gap-2 text-sm text-ink'>
                          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' className='text-accent'>
                            <path d='M20 6L9 17l-5-5' />
                          </svg>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <button
                      disabled={plan.current}
                      className={`w-full rounded-xl py-2.5 text-sm font-medium transition-all ${
                        plan.current
                          ? 'cursor-not-allowed bg-ink/[0.03] text-ink'
                          : plan.popular
                          ? 'bg-accent/15 text-ink hover:bg-accent/15'
                          : 'bg-ink/[0.03] text-ink hover:bg-ink/[0.05]'
                      }`}
                    >
                      {plan.current ? t('settings.currentPlan') : plan.id === 'enterprise' ? t('settings.contactSales') : t('settings.upgrade')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}
